// @ts-check

/** @file Offline timelog create/edit/delete against an open project replica. Each command writes the local domain row in one replica transaction for immediate feedback, then enqueues a single replay intent on the conflict-tracked SyncClient so the coordinator can replay, rebase, and reconcile it when connectivity returns. Only uninvoiced timelogs may be created, edited, or deleted offline: invoiced rows are server-owned financial records and are never mutated locally. */

/** @import {OpenProjectReplica} from "./project-replica.js" */

import Timelog from "../models/timelog.js"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER} from "./project-replica.js"
import {syncUuid} from "./project-replica-mutation-log.js"

/**
 * Local attributes an offline timelog may set. `invoiced` is intentionally
 * excluded — a timelog is never created, edited, or flipped to invoiced offline.
 * @typedef {object} OfflineTimelogChanges
 * @property {string | null} [comment] Replacement comment.
 * @property {string} [date] Replacement work date (YYYY-MM-DD).
 * @property {string | null} [taskId] Replacement task.
 * @property {number | null} [timeSeconds] Replacement worked duration in seconds.
 * @property {number | null} [timeTransportSeconds] Replacement transport duration in seconds.
 * @property {number | null} [transportLength] Kilometres travelled on the trip.
 */

/**
 * Reads the current authoritative updatedAt of a local record as an ISO string
 * base version, or null when the record has none.
 * @param {InstanceType<typeof Timelog>} timelog Local timelog record.
 * @returns {string | null} The base version, or null when the record has none.
 */
function baseVersionForTimelog(timelog) {
  const value = timelog.updatedAt()

  if (value instanceof Date) return value.toISOString()
  if (typeof value === "string" || typeof value === "number") return String(value)

  return null
}

/**
 * Normalizes a duration value to a non-negative integer number of seconds.
 * @param {number | null | undefined} value Raw duration.
 * @returns {number} Seconds, clamped to zero.
 */
function normalizedSeconds(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0
  return Math.max(0, Math.trunc(value))
}

/**
 * Normalizes a transport-length value to a non-negative integer.
 * @param {number | null | undefined} value Raw length.
 * @returns {number} Length, clamped to zero.
 */
function normalizedLength(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0
  return Math.max(0, Math.trunc(value))
}

/**
 * Normalizes a work-date value to a `YYYY-MM-DD` string, throwing when it is
 * absent or not a calendar date the server would accept.
 * @param {Date | string | null | undefined} value Raw date.
 * @returns {string} The canonical date string.
 * @throws {Error} When the date is missing or unparseable.
 */
function normalizedDate(value) {
  let date = value

  if (typeof date === "string") {
    const trimmed = date.trim()

    if (trimmed.length === 0) throw new Error("A timelog requires a work date.")
    date = new Date(`${trimmed}T00:00:00Z`)
  }

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new Error("A timelog requires a valid work date.")
  }

  return date.toISOString().slice(0, 10)
}

/**
 * Validates the worked/transport duration pair the same way the backend does:
 * a timelog must record at least one second of worked or transport time.
 * @param {{timeSeconds: number, timeTransportSeconds: number}} args Normalized durations.
 * @returns {void} Nothing when the pair is valid.
 * @throws {Error} When neither duration is positive.
 */
function assertHasDuration({timeSeconds, timeTransportSeconds}) {
  if (timeSeconds < 1 && timeTransportSeconds < 1) {
    throw new Error("A timelog must include worked time or transport time.")
  }
}

/**
 * Queues an offline timelog create. Inserts the local row (client-generated
 * UUID, invoiced=false) in one transaction, then enqueues a create-shaped
 * upsert intent that the coordinator replays through the resource's default
 * apply path (server-stamped userId, ability-checked). The client UUID makes a
 * duplicate replay idempotent.
 * @param {object} args Create inputs.
 * @param {OpenProjectReplica} args.replica Active project replica.
 * @param {string} args.taskId UUID of the task the time entry belongs to.
 * @param {string} args.date Work date (YYYY-MM-DD).
 * @param {number | null} args.timeSeconds Worked duration in seconds.
 * @param {number | null} [args.timeTransportSeconds] Transport duration in seconds.
 * @param {number | null} [args.transportLength] Kilometres travelled on the trip.
 * @param {string | null} [args.comment] Optional comment.
 * @param {string} args.userId Owner UUID (server re-stamps at replay).
 * @returns {Promise<{timelog: InstanceType<typeof Timelog>, timelogId: string}>} The local row and its UUID.
 * @throws {Error} When the replica is unusable or validation fails.
 */
export async function queueOfflineTimelogCreate({comment, date, replica, taskId, timeSeconds, timeTransportSeconds = 0, transportLength = 0, userId}) {
  const canonicalDate = normalizedDate(date)
  const workedSeconds = normalizedSeconds(timeSeconds)
  const transportSeconds = normalizedSeconds(timeTransportSeconds)
  const length = normalizedLength(transportLength)

  assertHasDuration({timeSeconds: workedSeconds, timeTransportSeconds: transportSeconds})

  const timelogId = syncUuid()
  const now = new Date().toISOString()

  /** @type {Record<string, unknown>} */
  const attributes = {
    comment: comment ?? null,
    createdAt: now,
    date: canonicalDate,
    id: timelogId,
    invoiced: false,
    taskId,
    timeSeconds: workedSeconds,
    timeTransportSeconds: transportSeconds,
    transportLength: length,
    updatedAt: now,
    userId
  }

  const timelog = await replica.handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    return await operation.forModel(Timelog).create(attributes)
  })

  /** @type {Record<string, unknown>} */
  const payload = {
    comment: comment ?? null,
    date: canonicalDate,
    taskId,
    timeSeconds: workedSeconds,
    timeTransportSeconds: transportSeconds,
    transportLength: length
  }

  await replica.syncClient.queue({data: payload, operation: "create", resource: timelog})

  return {timelog, timelogId}
}

/**
 * Queues an offline timelog edit. Captures the row's base version, refuses to
 * mutate an invoiced row, applies the local change for immediate feedback, then
 * enqueues a scalar update intent chained behind any pending predecessor for
 * the same timelog.
 * @param {object} args Edit inputs.
 * @param {OpenProjectReplica} args.replica Active project replica.
 * @param {string} args.timelogId Timelog UUID.
 * @param {OfflineTimelogChanges} args.changes Local change to apply and replay.
 * @returns {Promise<InstanceType<typeof Timelog>>} The updated local timelog.
 * @throws {Error} When the row is absent, invoiced, or validation fails.
 */
export async function queueOfflineTimelogEdit({changes, replica, timelogId}) {
  const updated = await replica.handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    const timelog = await operation.forModel(Timelog).findBy({id: timelogId})

    if (!timelog) throw new Error(`Project replica has no timelog ${timelogId}`)
    if (timelog.invoiced()) throw new Error("Invoiced timelogs can no longer be edited offline.")

    const baseVersion = baseVersionForTimelog(timelog)

    if (changes.comment !== undefined) timelog.setComment(changes.comment)
    if (changes.date !== undefined) timelog.setDate(normalizedDate(changes.date))
    if (changes.taskId !== undefined) timelog.setTaskId(changes.taskId)
    if (changes.timeSeconds !== undefined) timelog.setTimeSeconds(normalizedSeconds(changes.timeSeconds))
    if (changes.timeTransportSeconds !== undefined) timelog.setTimeTransportSeconds(normalizedSeconds(changes.timeTransportSeconds))
    if (changes.transportLength !== undefined) timelog.setTransportLength(normalizedLength(changes.transportLength))

    assertHasDuration({timeSeconds: normalizedSeconds(timelog.timeSeconds()), timeTransportSeconds: normalizedSeconds(timelog.timeTransportSeconds())})

    await timelog.save()

    return {baseVersion, timelog}
  })

  /** @type {Record<string, unknown>} */
  const payload = {}

  if (changes.comment !== undefined) payload.comment = changes.comment
  if (changes.date !== undefined) payload.date = normalizedDate(changes.date)
  if (changes.taskId !== undefined) payload.taskId = changes.taskId
  if (changes.timeSeconds !== undefined) payload.timeSeconds = normalizedSeconds(changes.timeSeconds)
  if (changes.timeTransportSeconds !== undefined) payload.timeTransportSeconds = normalizedSeconds(changes.timeTransportSeconds)
  if (changes.transportLength !== undefined) payload.transportLength = normalizedLength(changes.transportLength)

  await replica.syncClient.queue({baseVersion: updated.baseVersion, data: payload, operation: "update", resource: updated.timelog})

  return updated.timelog
}

/**
 * Queues an offline timelog delete. Refuses to destroy an invoiced row,
 * otherwise destroys the local row for immediate feedback and enqueues a delete
 * intent carrying the pre-delete base version so a genuine remote change still
 * surfaces as a conflict.
 * @param {object} args Delete inputs.
 * @param {OpenProjectReplica} args.replica Active project replica.
 * @param {string} args.timelogId Timelog UUID.
 * @returns {Promise<void>} Resolves after the intent is durable.
 * @throws {Error} When the row is absent or invoiced.
 */
export async function queueOfflineTimelogDelete({replica, timelogId}) {
  const {baseVersion, timelog} = await replica.handle.transaction({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async (operation) => {
    const record = await operation.forModel(Timelog).findBy({id: timelogId})

    if (!record) throw new Error(`Project replica has no timelog ${timelogId}`)
    if (record.invoiced()) throw new Error("Invoiced timelogs can no longer be deleted offline.")

    const version = baseVersionForTimelog(record)

    await record.destroy()

    return {baseVersion: version, timelog: record}
  })

  await replica.syncClient.queue({baseVersion, data: {id: timelogId}, operation: "destroy", resource: timelog})
}
